
import React from 'react';
import { Journey, JourneyAnswerSet } from '@shared/types';
import { speakText } from '@shared/utils/voice';

interface Props {
  journey: Journey;
  onBack: () => void;
  userCredits: number;
  answerSets: JourneyAnswerSet[];
  onGenerateAnswers: () => Promise<void>;
}

const renderBoldText = (value: string) => {
  const safeValue = value || "";
  const parts = safeValue.split("**");
  return parts.map((part, idx) => idx % 2 === 1 ? (
    <strong key={idx} className="font-black">{part}</strong>
  ) : (
    <span key={idx}>{part}</span>
  ));
};

const JourneyDetail: React.FC<Props> = ({ journey, onBack, userCredits, answerSets, onGenerateAnswers }) => {
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [showNoCreditsTip, setShowNoCreditsTip] = React.useState(false);
  const [activeAnswerIndex, setActiveAnswerIndex] = React.useState(0);
  const cost = journey.questions.length;
  const hasCredits = userCredits >= cost;

  React.useEffect(() => {
    if (!isGenerating) return;
    setActiveAnswerIndex(0);
    const total = journey.questions.length || 1;
    const timer = setInterval(() => {
      setActiveAnswerIndex(prev => (prev + 1 < total ? prev + 1 : prev));
    }, 900);
    return () => clearInterval(timer);
  }, [isGenerating, journey.questions.length]);

  const handleConfirmGenerate = async () => {
    setIsGenerating(true);
    try {
      await onGenerateAnswers();
      setShowConfirm(false);
    } catch (err: any) {
      if (err?.message === "INSUFFICIENT_CREDITS") {
        setShowConfirm(false);
        setShowNoCreditsTip(true);
        setTimeout(() => setShowNoCreditsTip(false), 2500);
      } else {
        alert("Answer generation failed. Please try again.");
      }
    } finally {
      setIsGenerating(false);
    }
  };
  return (
    <>
    <div className="p-8 print:hidden">
      <button onClick={onBack} className="text-blue-600 font-medium mb-4 flex items-center gap-1 hover:underline">
        Back to Dashboard
      </button>

      <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
        <div className="flex-grow">
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-1 rounded">{journey.grade}</span>
            <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded">{journey.interviewType}</span>
            {journey.isRealInterview && (
              <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded">Real Interview</span>
            )}
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">{journey.specialty} Specialist Pack</h1>
          <p className="text-slate-500 max-w-2xl">
            Customized for {journey.requirements?.roleTitle || 'General Application'} at {journey.requirements?.trustName || 'NHS Trust'}. 
            Extracted {journey.requirements?.essentialCriteria?.length || 0} essential criteria.
          </p>
        </div>
        {!journey.isPublic && (
          <div className="flex items-center gap-3">
            <div
              className="relative"
              onClick={() => {
                if (!hasCredits) {
                  setShowNoCreditsTip(true);
                  setTimeout(() => setShowNoCreditsTip(false), 2500);
                  return;
                }
                setShowConfirm(true);
              }}
            >
              <button
                disabled={!hasCredits}
                className={`px-6 py-3 rounded-xl font-bold shadow-lg border transition-all ${hasCredits ? 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50' : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'}`}
              >
                Generate Answers
              </button>
              {showNoCreditsTip && (
                <div className="absolute right-0 mt-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest px-3 py-2 rounded-lg shadow-xl">
                  Not enough credits
                </div>
              )}
            </div>
            <button
              onClick={() => window.print()}
              className="bg-white text-slate-700 px-6 py-3 rounded-xl font-bold shadow-lg border border-slate-200 hover:bg-slate-50 transition-all"
            >
              Download PDF
            </button>
          </div>
        )}
        {journey.isPublic && (
          <div className="bg-emerald-50 border-2 border-emerald-200 text-emerald-700 px-8 py-3 rounded-xl font-bold text-center">
            <div className="flex items-center justify-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>
              </svg>
              Public Template - View Only
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="w-1.5 h-6 bg-blue-500 rounded"></span>
              Extracted Requirements
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Essential Criteria</p>
                <ul className="space-y-1">
                  {journey.requirements?.essentialCriteria?.slice(0, 5).map((c, i) => (
                    <li key={i} className="text-sm text-slate-600 flex gap-2">
                      <span className="text-blue-500 mt-1">*</span>
                      {c}
                    </li>
                  ))}
                  {(!journey.requirements?.essentialCriteria || journey.requirements.essentialCriteria.length === 0) && (
                    <li className="text-sm text-slate-400 italic">No specific criteria extracted.</li>
                  )}
                </ul>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Keywords</p>
                <div className="flex flex-wrap gap-2">
                  {journey.requirements?.keywords?.map((k, i) => (
                    <span key={i} className="text-xs bg-white border border-slate-200 px-2 py-1 rounded text-slate-600">{k}</span>
                  ))}
                  {(!journey.requirements?.keywords || journey.requirements.keywords.length === 0) && (
                    <span className="text-xs text-slate-400 italic">None identified.</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <span className="w-1.5 h-6 bg-green-500 rounded"></span>
            AI-Generated Questions ({journey.questions?.length || 0})
          </h3>
          <div className="space-y-3">
            {journey.questions?.map((q, idx) => (
              <div key={q.id} className="p-4 bg-white border border-slate-100 rounded-xl shadow-sm hover:border-blue-200 transition-colors group">
                <div className="flex justify-between mb-2">
                  <div className="flex gap-2">
                    {q.domainTags?.map(tag => (
                      <span key={tag} className="text-[10px] uppercase tracking-tighter font-bold bg-slate-50 text-slate-500 px-1.5 py-0.5 rounded">{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${q.difficulty === 'Hard' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                      {q.difficulty}
                    </span>
                    <button 
                      onClick={() => speakText(q.text)}
                      className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-700 transition-all p-1"
                      title="Listen"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                    </button>
                  </div>
                </div>
                <h4 className="font-semibold text-slate-700 leading-tight">{q.text}</h4>
                {answerSets.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {answerSets.map((set, setIdx) => {
                      const answer = set.answers[q.id];
                      if (!answer) return null;
                      return (
                        <div key={set.id || `${setIdx}-${q.id}`} className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-sm text-emerald-800 leading-relaxed">
                          <div className="text-[9px] font-black text-emerald-700 uppercase tracking-widest mb-2">
                            AI Answer {answerSets.length - setIdx}
                          </div>
                          {renderBoldText(answer)}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    {showConfirm && (
      <div className="fixed inset-0 z-[70] bg-slate-900/70 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl">
          <h3 className="text-xl font-black text-slate-900 mb-3">Generate AI Answers?</h3>
          <p className="text-sm text-slate-600 mb-6">
            This will generate answers for all {cost} questions and deduct {cost} credits from your account.
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setShowConfirm(false)}
              disabled={isGenerating}
              className="px-5 py-2 rounded-xl font-bold text-slate-500 hover:text-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmGenerate}
              disabled={isGenerating}
              className="bg-slate-900 text-white px-6 py-2 rounded-xl font-bold hover:bg-blue-600 disabled:opacity-60"
            >
              {isGenerating ? 'Generating...' : 'Generate Answers'}
            </button>
          </div>
          {isGenerating && (
            <div className="mt-6 border-t border-slate-100 pt-5">
              <div className="flex items-center gap-3 text-xs font-bold text-slate-500 uppercase tracking-widest">
                <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                Generating answer {activeAnswerIndex + 1} / {cost}
              </div>
              <div className="mt-4 max-h-56 overflow-y-auto space-y-2 text-xs text-slate-600">
                {journey.questions.map((q, idx) => {
                  const isDone = idx < activeAnswerIndex;
                  const isActive = idx === activeAnswerIndex;
                  return (
                    <div key={q.id} className={`flex items-start gap-2 ${isActive ? 'text-slate-900 font-semibold' : ''}`}>
                      <span className={`mt-0.5 text-[10px] font-black ${isDone ? 'text-emerald-600' : isActive ? 'text-blue-600' : 'text-slate-300'}`}>
                        {isDone ? 'DONE' : isActive ? 'NOW' : 'WAIT'}
                      </span>
                      <span className="line-clamp-2">{q.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    )}
    <div className="hidden print:block bg-white text-slate-900">
      <div className="p-8 space-y-8">
        <div className="flex items-start justify-between border-b border-slate-200 pb-4">
          <div>
            <h1 className="text-2xl font-black">Blueprint Export</h1>
            <p className="text-sm text-slate-500 font-medium">
              {journey.specialty} - {journey.grade} - {journey.interviewType}
            </p>
          </div>
          <div className="text-xs text-slate-500 font-semibold text-right">
            <div>{new Date().toLocaleDateString('en-GB')}</div>
            <div>Stations: {journey.questions?.length || 0}</div>
          </div>
        </div>
        <div className="flex justify-end">
          <img src="/forpdf.png" alt="NHS Interview Academy" className="h-10 w-auto object-contain" />
        </div>

        <div className="space-y-6">
          <div className="border border-slate-200 rounded-2xl p-5">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Essential Criteria</p>
            {journey.requirements?.essentialCriteria?.length ? (
              <ul className="space-y-1 text-xs text-slate-700">
                {journey.requirements.essentialCriteria.map((c, i) => (
                  <li key={i}>- {c}</li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500 italic">No essential criteria available.</p>
            )}
          </div>

          <div className="space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">AI-Generated Questions</p>
            {journey.questions?.map((q, idx) => (
              <div key={q.id} className="border border-slate-200 rounded-2xl p-5">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Station {idx + 1}</p>
                <h2 className="text-base font-black text-slate-900 mt-1">{q.text}</h2>
                <div className="mt-2 flex flex-wrap gap-2">
                  {q.domainTags?.map(tag => (
                    <span key={tag} className="text-[9px] font-black uppercase bg-slate-50 text-slate-500 px-2 py-1 rounded">{tag}</span>
                  ))}
                  <span className={`text-[9px] font-black uppercase px-2 py-1 rounded ${q.difficulty === 'Hard' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                    {q.difficulty}
                  </span>
                </div>
                {answerSets.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {answerSets.map((set, setIdx) => {
                      const answer = set.answers[q.id];
                      if (!answer) return null;
                      return (
                        <div key={set.id || `print-${setIdx}-${q.id}`} className="border border-emerald-100 rounded-xl p-4 bg-emerald-50">
                          <p className="text-[9px] font-black text-emerald-700 uppercase tracking-widest mb-2">
                            AI Answer {answerSets.length - setIdx}
                          </p>
                          <p className="text-xs text-emerald-900 leading-relaxed whitespace-pre-line">
                            {renderBoldText(answer)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default JourneyDetail;
